/* iter55g50 — Author Business Dashboard.
 *
 * Full-page view at `/app/business` that turns the writer's data into
 * a P&L view of their creative work:
 *
 *   1. Top KPI strip — total words / books live / lifetime earnings /
 *      net profit (earnings − LLM cost − projected marketing spend).
 *   2. Writing progress — per-project rows: chapters, words, target,
 *      % complete, LLM cost so far.
 *   3. Sales & earnings per book — royalties ledger view.
 *   4. Marketing marketplace — catalog of platforms authors can opt
 *      into (TikTok BookTok, Meta Ads, Amazon Ads, BookBub, etc.) plus
 *      the author's current opt-ins with monthly budgets.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, BookOpen, TrendingUp, DollarSign, Wallet, Loader2, PenLine,
  ShoppingBag, BadgeCheck, Music2, Facebook, Instagram, Mail, Mic, Star, Plus,
  X as XIcon, ExternalLink, MessageCircle, Send, Bell, BellOff, Clock,
  Link2, Unlink, Zap, AlertTriangle, TrendingDown, Video, Trophy, Target, BriefcaseBusiness,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { businessApi } from '@/lib/api';
import { useAuth } from '@/lib/auth_context';
import axios from 'axios';   // iter-ads1 — direct calls for new endpoints
import { HelpTip } from '@/components/HelpTip';   // iter-p2-e
import MyTaxProfileCard from '@/components/MyTaxProfileCard';
import MyContractsCard from '@/components/MyContractsCard';
import AuthorAgreementCard from '@/components/AuthorAgreementCard';
import PublishingElectionCard from '@/components/PublishingElectionCard';
import BusinessCenterDrawer from '@/components/business/BusinessCenterDrawer';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const ICONS = { BookOpen, ShoppingBag, BadgeCheck, Music2, Facebook, Instagram, Mail, Mic, Star };
const usd = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

// ── ProjectRing ─────────────────────────────────────────────────────────────
// SVG ring showing overall % complete toward the project's target word count.
function ProjectRing({ pct = 0 }) {
  const r = 9;
  const circ = 2 * Math.PI * r;
  const dash = (Math.min(100, pct) / 100) * circ;
  const done = pct >= 100;
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" className="shrink-0"
      aria-label={`${Math.round(pct)}% complete`}>
      <circle cx="12" cy="12" r={r} fill="none" strokeWidth="2.5"
        className="stroke-secondary" />
      {pct > 0 && (
        <circle cx="12" cy="12" r={r} fill="none" strokeWidth="2.5"
          stroke={done ? '#22c55e' : 'hsl(var(--primary))'}
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 12 12)" />
      )}
    </svg>
  );
}
const cents = (dollars) => Math.round(Number(dollars || 0) * 100);
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return iso; }
};

function KpiCard({ icon, label, value, sub, tone = 'default' }) {
  const toneClass =
    tone === 'positive' ? 'text-emerald-600 dark:text-emerald-400' :
    tone === 'negative' ? 'text-rose-600 dark:text-rose-400' :
    'text-foreground';
  return (
    <Card className="p-4">
      <div className="mb-1 flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-muted-foreground">
        {icon} {label}
      </div>
      <div className={`font-mono-stat text-2xl font-semibold ${toneClass}`}>{value}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </Card>
  );
}

function Section({ title, subtitle, children, right }) {
  return (
    <section className="space-y-3">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold">{title}</h2>
          {subtitle && <p className="text-[12px] text-muted-foreground">{subtitle}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

function PlatformCard({ platform, myOptIn, onOptIn, onRemove, onUpdateBudget, onBookService }) {
  const Icon = ICONS[platform.icon] || BookOpen;
  const isActive = !!myOptIn && myOptIn.status !== 'cancelled';
  const [budgetDraft, setBudgetDraft] = useState(
    myOptIn ? (Number(myOptIn.monthly_budget_cents || 0) / 100).toString() : '',
  );

  return (
    <Card className={'p-4 transition ' + (isActive ? 'border-primary/60 bg-primary/5' : '')}
          data-testid={`marketing-platform-${platform.id}`}>
      <div className="mb-2 flex items-start gap-2">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-sm bg-primary/10 text-primary">
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <div className="truncate text-[13px] font-semibold">{platform.name}</div>
            {platform.url && (
              <a href={platform.url} target="_blank" rel="noreferrer"
                 className="text-muted-foreground hover:text-primary"
                 title="Open platform">
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
            {platform.category}
          </div>
        </div>
      </div>
      <p className="mb-2 line-clamp-3 text-[11.5px] leading-snug text-muted-foreground">
        {platform.description}
      </p>
      <div className="mb-3 space-y-0.5 text-[11px]">
        <div className="flex justify-between">
          <span className="text-muted-foreground">Monthly range</span>
          <span className="font-mono-stat">
            {usd(platform.monthly_cost_low_cents)} – {usd(platform.monthly_cost_high_cents)}
          </span>
        </div>
        {platform.estimated_reach && (
          <div className="flex justify-between">
            <span className="text-muted-foreground">Est. reach</span>
            <span className="truncate pl-2 font-mono-stat">{platform.estimated_reach}</span>
          </div>
        )}
      </div>

      {isActive ? (
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <Input
              value={budgetDraft}
              onChange={(e) => setBudgetDraft(e.target.value)}
              placeholder="Monthly $"
              inputMode="decimal"
              className="h-7 flex-1 text-[11.5px]"
              data-testid={`marketing-budget-${platform.id}`}
            />
            <Button size="sm" variant="outline" className="h-7 text-[10.5px]"
                    onClick={() => onUpdateBudget(myOptIn, cents(budgetDraft))}
                    data-testid={`marketing-budget-save-${platform.id}`}>
              Save
            </Button>
          </div>
          <Button size="sm" variant="ghost" className="h-7 w-full gap-1 text-[10.5px] text-rose-600"
                  onClick={() => onRemove(myOptIn)}
                  data-testid={`marketing-opt-out-${platform.id}`}>
            <XIcon className="h-3 w-3" /> Opt out
          </Button>
        </div>
      ) : (
        <div className="space-y-1.5">
          <Button size="sm" className="h-7 w-full gap-1 text-[11px]"
                  onClick={() => onOptIn(platform)}
                  data-testid={`marketing-opt-in-${platform.id}`}>
            <Plus className="h-3 w-3" /> Add to plan
          </Button>
          {platform.bookable && (
            <Button size="sm" variant="outline"
                    className="h-7 w-full gap-1 text-[10.5px] border-primary/50 text-primary hover:bg-primary/10"
                    onClick={() => onBookService(platform)}
                    data-testid={`marketing-book-${platform.id}`}
                    title="We invoice, we run it — through Stripe">
              Book through us
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

export default function BusinessDashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [busy, setBusy]     = useState(false);
  const [businessCenterOpen, setBusinessCenterOpen] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bookingsTab, setBookingsTab] = useState('all');
  // iter55g56 — Per-row inline reply drafts + email prefs
  const [replyDraft, setReplyDraft] = useState({});
  const [expandedTimelineId, setExpandedTimelineId] = useState(null);
  const [emailPref, setEmailPref] = useState(true);

  const reload = async () => {
    setLoad(true);
    try {
      const [r, b, ep] = await Promise.all([
        businessApi.dashboard(),
        businessApi.bookings().catch(() => ({ bookings: [] })),
        businessApi.emailPrefsGet().catch(() => ({ email_ops_notes: true })),
      ]);
      setData(r);
      setBookings(b?.bookings || []);
      setEmailPref(!!ep?.email_ops_notes);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load your dashboard.');
    } finally { setLoad(false); }
  };

  const toggleEmailPref = async () => {
    const next = !emailPref;
    setEmailPref(next);
    try {
      await businessApi.emailPrefsSet(next);
      toast.success(next ? 'You\u2019ll get ops-note emails.' : 'Ops-note emails muted.');
    } catch (e) {
      setEmailPref(!next);
      toast.error(e?.response?.data?.detail || 'Could not save preference.');
    }
  };

  const sendReply = async (bookingId) => {
    const text = (replyDraft[bookingId] || '').trim();
    if (!text) return;
    try {
      await businessApi.bookingReply(bookingId, text);
      setReplyDraft((s) => ({ ...s, [bookingId]: '' }));
      toast.success('Reply sent to ops.');
      // iter55h — Sending a reply implies the author has seen everything so far.
      try { await businessApi.bookingMarkSeen(bookingId); } catch { /* ignore */ }
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not send reply.');
    }
  };

  // iter55h — Compute unread ops replies per booking. A reply is unread
  // when its timestamp is later than the author's `ops_thread_seen_at`
  // (or the author has never marked the thread seen and there is at
  // least one ops reply).
  const unreadOpsCount = (b) => {
    const replies = b.ops_replies || [];
    if (!replies.length) return 0;
    const seen = b.ops_thread_seen_at || '';
    if (!seen) return replies.length;
    return replies.filter((r) => (r.at || '') > seen).length;
  };

  const markThreadSeen = async (bookingId) => {
    try {
      await businessApi.bookingMarkSeen(bookingId);
      // Local optimistic update so the red dot vanishes immediately.
      setBookings((prev) => prev.map((b) =>
        b.id === bookingId
          ? { ...b, ops_thread_seen_at: new Date().toISOString() }
          : b,
      ));
    } catch { /* silent */ }
  };

  useEffect(() => { reload(); /* eslint-disable-line */ }, []);

  // iter55j — Unread Sound Ping. Play a soft Web-Audio chime whenever
  // the unread ops-reply count rises while the author is on this page.
  // Author can mute via the bell button (persisted to localStorage).
  const [soundMuted, setSoundMuted] = useState(() => {
    try { return localStorage.getItem('biz_ping_muted') === '1'; }
    catch { return false; }
  });
  const prevUnreadRef = useRef(0);
  useEffect(() => {
    const totalUnread = bookings.reduce((n, b) => n + unreadOpsCount(b), 0);
    if (totalUnread > prevUnreadRef.current && !soundMuted && prevUnreadRef.current !== null) {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (Ctx) {
          const ctx = new Ctx();
          const gain = ctx.createGain();
          gain.connect(ctx.destination);
          gain.gain.setValueAtTime(0.0001, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.11, ctx.currentTime + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.55);
          // Two-tone chime (perfect fifth) for a "message received" vibe.
          [880, 1318.5].forEach((hz, i) => {
            const osc = ctx.createOscillator();
            osc.type = 'sine';
            osc.frequency.value = hz;
            osc.connect(gain);
            osc.start(ctx.currentTime + i * 0.08);
            osc.stop(ctx.currentTime + 0.55);
          });
          window.setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 900);
        }
      } catch { /* Web Audio unsupported / not allowed yet */ }
    }
    prevUnreadRef.current = totalUnread;
  }, [bookings, soundMuted]);

  const optInByPlatform = useMemo(() => {
    const m = {};
    (data?.marketing?.opt_ins || []).forEach((o) => { m[o.platform_id] = o; });
    return m;
  }, [data]);

  const onOptIn = async (platform) => {
    setBusy(true);
    try {
      await businessApi.optIn(platform.id);
      toast.success(`Added ${platform.name} to your plan.`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not opt in.');
    } finally { setBusy(false); }
  };
  const onRemove = async (opt) => {
    setBusy(true);
    try {
      await businessApi.optOut(opt.id);
      toast.success('Opted out.');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not opt out.');
    } finally { setBusy(false); }
  };
  const onUpdateBudget = async (opt, budgetCents) => {
    setBusy(true);
    try {
      await businessApi.updateOptIn(opt.id, { monthly_budget_cents: budgetCents });
      toast.success('Budget updated.');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update budget.');
    } finally { setBusy(false); }
  };
  const onBookService = async (platform) => {
    const lo = Math.round(Number(platform.monthly_cost_low_cents || 10000) / 100);
    const raw = window.prompt(
      `How much would you like to spend on "${platform.name}" (USD)?\n\nSuggested range: $${lo}–$${Math.round(Number(platform.monthly_cost_high_cents || 100000) / 100)}.`,
      String(lo),
    );
    if (!raw) return;
    const usd = Number(raw);
    if (!Number.isFinite(usd) || usd < 10) {
      toast.error('Enter a number of at least $10.');
      return;
    }
    setBusy(true);
    try {
      const r = await businessApi.bookService(platform.id,
                                                { amount_cents: Math.round(usd * 100) });
      if (r?.checkout_url) {
        window.location.href = r.checkout_url;
      } else {
        toast.error('Booking created but no Stripe URL was returned.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start booking.');
    } finally { setBusy(false); }
  };

  if (loading || !data) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  const k = data.kpis || {};
  const netTone = k.net_profit_cents >= 0 ? 'positive' : 'negative';

  return (
    <div className="h-screen w-full overflow-y-auto overflow-x-hidden bg-background text-foreground" data-testid="business-dashboard">
      <header className="sticky top-0 z-10 border-b border-border bg-panel-header/95 px-6 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => nav('/app')}
                  data-testid="business-back-btn" className="gap-1.5">
            <ArrowLeft className="h-3.5 w-3.5" /> Workspace
          </Button>
          <div className="flex-1">
            <h1 className="text-[15px] font-semibold">Author Business Dashboard</h1>
            <div className="text-[11px] text-muted-foreground">
              Hi {user?.name || 'writer'} — every book is a business. Here&apos;s the P&amp;L.
            </div>
          </div>
          {/* iter55g56 — Ops-note email toggle. */}
          <button type="button"
                  onClick={toggleEmailPref}
                  data-testid="business-email-pref-toggle"
                  className={
                    'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] transition ' +
                    (emailPref
                      ? 'border-primary/40 bg-primary/5 text-primary hover:bg-primary/10'
                      : 'border-border bg-card text-muted-foreground hover:border-primary/40')
                  }
                  title={emailPref ? 'Ops-note emails ON — click to mute' : 'Ops-note emails MUTED — click to enable'}>
            {emailPref ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
            Ops emails
          </button>
          <button type="button" onClick={() => setBusinessCenterOpen(true)} data-testid="business-dashboard-open-center" className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-[11px] font-semibold text-primary-foreground"><BriefcaseBusiness className="h-3.5 w-3.5" /> Business Center</button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-8 px-6 py-6">
        {/* KPI strip */}
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="business-kpis">
          <KpiCard icon={<PenLine className="h-3 w-3" />}
                   label="Words written"
                   value={(k.words_total || 0).toLocaleString()}
                   sub={k.words_target ? `of ${k.words_target.toLocaleString()} target · ${k.words_pct_complete}%` : 'across all projects'} />
          <KpiCard icon={<BookOpen className="h-3 w-3" />}
                   label="Books live"
                   value={k.books_live || 0}
                   sub={`${k.total_sales || 0} lifetime sales`} />
          <KpiCard icon={<DollarSign className="h-3 w-3" />}
                   label="Lifetime earnings"
                   value={usd(k.lifetime_earnings_cents)}
                   sub={`${usd(k.lifetime_llm_cost_cents)} in LLM costs`} />
          <KpiCard icon={<TrendingUp className="h-3 w-3" />}
                   label="Net profit"
                   value={usd(k.net_profit_cents)}
                   sub={k.monthly_marketing_cents > 0
                     ? `after ${usd(k.monthly_marketing_cents)}/mo marketing`
                     : 'earnings − LLM cost'}
                   tone={netTone} />
        </div>

        {/* Author-facing tax profile — feeds admin Tax Center + Stripe payouts */}
        <MyTaxProfileCard />
        <AuthorAgreementCard />
        <MyContractsCard />

        {/* Writing progress */}
        <Section title="Writing progress"
                 subtitle="Every project you have on the go, sized against its own target.">
          {data.writing.length === 0 ? (
            <Card className="p-6 text-center text-[12px] text-muted-foreground">
              You haven&apos;t started a project yet. <a href="/app" className="text-primary underline">Open your Desk</a>.
            </Card>
          ) : (
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-[12px]">
                <thead className="bg-panel-header/40">
                  <tr className="text-left text-[10.5px] uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2">Project</th>
                    <th className="px-3 py-2 text-right">Words</th>
                    <th className="px-3 py-2 text-right">% done</th>
                    <th className="px-3 py-2 text-right">Chapters</th>
                    <th className="px-3 py-2 text-right">LLM cost</th>
                  </tr>
                </thead>
                <tbody>
                  {data.writing.map((p) => (
                    <tr key={p.project_id} className="border-t border-border"
                        data-testid={`business-writing-row-${p.project_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{p.name}</div>
                        <div className="text-[10.5px] text-muted-foreground">{p.genre}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono-stat">
                        {p.words.toLocaleString()}
                        <div className="text-[10px] text-muted-foreground">
                          / {p.target_words.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono-stat">
                        {/* Project summary ring — visual progress toward target word count */}
                        <div className="flex items-center justify-end gap-1.5">
                          <ProjectRing pct={p.pct_complete || 0} />
                          <span>{p.pct_complete}%</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono-stat">
                        {p.chapters_drafted} / {p.chapters_planned}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-stat">
                        {usd(p.llm_cost_cents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {data.writing.length > 0 && (
          <Section title="Publishing elections"
                   subtitle="Choose how each manuscript will enter the marketplace before publication.">
            <div className="grid gap-3 lg:grid-cols-2" data-testid="business-publishing-elections">
              {data.writing.map((projectRow) => (
                <PublishingElectionCard key={projectRow.project_id} projectId={projectRow.project_id} />
              ))}
            </div>
          </Section>
        )}

        {/* Sales & earnings */}
        <Section title="Sales & earnings per book"
                 subtitle="Your 85% share from every direct-split checkout, per title.">
          {data.books.length === 0 ? (
            <Card className="p-6 text-center text-[12px] text-muted-foreground">
              None of your books are live yet. Publish from the Bookstore admin panel.
            </Card>
          ) : (
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-[12px]">
                <thead className="bg-panel-header/40">
                  <tr className="text-left text-[10.5px] uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2">Book</th>
                    <th className="px-3 py-2 text-right">Price</th>
                    <th className="px-3 py-2 text-right">Sales</th>
                    <th className="px-3 py-2 text-right">Earned</th>
                    <th className="px-3 py-2 text-right">LLM cost</th>
                    <th className="px-3 py-2 text-right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {data.books.map((b) => (
                    <tr key={b.book_id} className="border-t border-border"
                        data-testid={`business-book-row-${b.book_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{b.title}</div>
                        <div className="text-[10.5px] text-muted-foreground">{b.genre}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono-stat">{usd(b.price_cents)}</td>
                      <td className="px-3 py-2 text-right font-mono-stat">{b.sales}</td>
                      <td className="px-3 py-2 text-right font-mono-stat">{usd(b.author_cents)}</td>
                      <td className="px-3 py-2 text-right font-mono-stat">{usd(b.llm_cost_cents)}</td>
                      <td className={'px-3 py-2 text-right font-mono-stat ' +
                        (b.net_cents >= 0 ? 'text-emerald-600' : 'text-rose-600')}>
                        {usd(b.net_cents)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>

        {/* Marketing marketplace */}
        <Section title="Marketing marketplace"
                 subtitle="Opt in to promote your books. Set a monthly budget per platform — track spend here."
                 right={
                   <div className="text-[11px] text-muted-foreground" data-testid="marketing-monthly-total">
                     Total budget: <span className="font-mono-stat font-semibold">
                       {usd(data.marketing?.monthly_budget_cents)}
                     </span> / month
                   </div>
                 }>
          {(data.marketing?.catalog || []).length === 0 ? (
            <Card className="p-6 text-center text-[12px] text-muted-foreground">
              No platforms in the catalog yet.
            </Card>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {data.marketing.catalog.map((p) => (
                <PlatformCard key={p.id} platform={p}
                              myOptIn={optInByPlatform[p.id]}
                              onOptIn={busy ? () => {} : onOptIn}
                              onRemove={busy ? () => {} : onRemove}
                              onUpdateBudget={busy ? () => {} : onUpdateBudget}
                              onBookService={busy ? () => {} : onBookService} />
              ))}
            </div>
          )}
        </Section>

        {/* iter-ads1 — Amazon Ads real-API connect card + click analyzer.
             These two sections turn the Business Dashboard from a static
             P&L view into a real creator business center. */}
        <AmazonAdsConnectCard />
        <MetaAdsConnectCard />
        <TikTokAdsConnectCard />
        <AdsSummarySection />
        <ClickAnalyzer />
        {/* iter-p2-n — Reader Journey Analytics + Series Bundler + Reader Magnet toggles */}
        <ReaderJourneyAnalyticsCard />
        <SeriesBundlerCard />
        <ReaderMagnetToggleCard />

        {/* Recent bookings (with status filter + ops notes) */}
        {bookings.length > 0 && (
          <Section
            title="Recent bookings"
            subtitle="Managed services you booked through us. Stripe receipts open in a new tab."
            right={
              <div className="flex flex-wrap items-center gap-1.5" data-testid="business-bookings-tabs">
                <button
                  type="button"
                  onClick={() => {
                    setSoundMuted((m) => {
                      const next = !m;
                      try { localStorage.setItem('biz_ping_muted', next ? '1' : '0'); } catch { /* noop */ }
                      toast.success(next ? 'Reply chime muted.' : 'Reply chime on — you\u2019ll hear new ops replies live.');
                      return next;
                    });
                  }}
                  data-testid="business-bookings-mute"
                  title={soundMuted ? 'Chime is muted — click to enable' : 'Chime on — click to mute'}
                  className={
                    'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10.5px] transition ' +
                    (soundMuted
                      ? 'border-border bg-card text-muted-foreground hover:border-primary/40'
                      : 'border-primary bg-primary/10 text-primary')
                  }
                >
                  {soundMuted ? <BellOff className="h-3 w-3" /> : <Bell className="h-3 w-3" />}
                  {soundMuted ? 'Muted' : 'Chime on'}
                </button>
                {['all', 'pending', 'paid', 'fulfilled', 'cancelled'].map((s) => {
                  const n = s === 'all' ? bookings.length : bookings.filter((b) => b.status === s).length;
                  if (s !== 'all' && n === 0) return null;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setBookingsTab(s)}
                      data-testid={`business-bookings-tab-${s}`}
                      className={
                        'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10.5px] transition ' +
                        (bookingsTab === s
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-card hover:border-primary/40 text-muted-foreground')
                      }
                    >
                      {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                      <span className="rounded-sm bg-muted px-1 text-[9.5px] font-mono">{n}</span>
                    </button>
                  );
                })}
              </div>
            }
          >
            <div className="overflow-hidden rounded-md border border-border">
              <table className="w-full text-[12px]">
                <thead className="bg-panel-header/40">
                  <tr className="text-left text-[10.5px] uppercase tracking-wider text-muted-foreground">
                    <th className="px-3 py-2">Booked</th>
                    <th className="px-3 py-2">Platform / Ops note</th>
                    <th className="px-3 py-2 text-right">Amount</th>
                    <th className="px-3 py-2 text-right">Status</th>
                    <th className="px-3 py-2 text-right">Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings
                    .filter((b) => bookingsTab === 'all' || b.status === bookingsTab)
                    .map((b) => {
                    const unread = unreadOpsCount(b);
                    return (
                    <tr key={b.id} className="border-t border-border"
                        data-testid={`business-booking-row-${b.id}`}>
                      <td className="px-3 py-2 font-mono-stat text-[10.5px] text-muted-foreground">
                        {fmtDate(b.paid_at || b.created_at)}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="font-medium">{b.platform_name}</div>
                          {unread > 0 && (
                            <button type="button"
                                    onClick={() => markThreadSeen(b.id)}
                                    title={`${unread} new reply from ops — click to mark seen`}
                                    data-testid={`business-booking-unread-${b.id}`}
                                    className="inline-flex items-center gap-1 rounded-full bg-rose-500 px-1.5 py-0.5 text-[9.5px] font-semibold text-white hover:bg-rose-600">
                              <span className="h-1.5 w-1.5 rounded-full bg-white/90" />
                              {unread} new
                            </button>
                          )}
                        </div>
                        {b.ops_note && (
                          <div className="mt-0.5 text-[10.5px] italic text-primary/80"
                               data-testid={`business-booking-note-${b.id}`}>
                            💬 {b.ops_note}
                          </div>
                        )}
                        {/* iter55g56 — inline reply + timeline expand */}
                        <div className="mt-1.5 flex items-center gap-2">
                          <input
                            value={replyDraft[b.id] || ''}
                            onChange={(e) => setReplyDraft((s) => ({ ...s, [b.id]: e.target.value }))}
                            placeholder="Reply to ops…"
                            className="h-6 flex-1 rounded-sm border border-border bg-card px-2 text-[10.5px]"
                            data-testid={`business-booking-reply-input-${b.id}`}
                          />
                          <button type="button"
                                  onClick={() => sendReply(b.id)}
                                  className="inline-flex items-center gap-1 rounded-sm border border-border bg-card px-1.5 py-0.5 text-[9.5px] hover:border-primary/40 hover:text-primary"
                                  data-testid={`business-booking-reply-send-${b.id}`}>
                            <Send className="h-2.5 w-2.5" /> Send
                          </button>
                          {(b.status_history?.length || 0) > 0 && (
                            <button type="button"
                                    onClick={() => setExpandedTimelineId(expandedTimelineId === b.id ? null : b.id)}
                                    className="inline-flex items-center gap-1 rounded-sm border border-border bg-card px-1.5 py-0.5 text-[9.5px] hover:border-primary/40 hover:text-primary"
                                    data-testid={`business-booking-timeline-toggle-${b.id}`}>
                              <Clock className="h-2.5 w-2.5" /> {b.status_history.length} step{b.status_history.length === 1 ? '' : 's'}
                            </button>
                          )}
                        </div>
                        {(() => {
                          // iter55h — Merge author + ops replies into one thread.
                          const ar = (b.author_replies || []).map((r) => ({ ...r, actor: 'author' }));
                          const op = (b.ops_replies    || []).map((r) => ({ ...r, actor: 'admin'  }));
                          const thread = [...ar, ...op].sort(
                            (a, x) => (a.at || '').localeCompare(x.at || ''),
                          );
                          if (thread.length === 0) return null;
                          return (
                            <div className="mt-1 space-y-0.5 text-[10.5px]"
                                 data-testid={`business-booking-thread-${b.id}`}>
                              {thread.map((r, i) => {
                                const isOps = r.actor === 'admin';
                                return (
                                  <div key={`${b.id}-msg-${i}`}
                                       className={
                                         'flex items-center gap-1 ' +
                                         (isOps ? 'text-primary' : 'text-muted-foreground')
                                       }
                                       data-testid={`business-booking-thread-msg-${b.id}-${i}`}>
                                    <MessageCircle className="h-2.5 w-2.5" />
                                    <span className={isOps ? 'font-medium' : 'italic'}>
                                      {isOps ? 'Ops' : 'You'}: {r.text}
                                    </span>
                                    <span className="text-[9.5px] opacity-60">· {fmtDate(r.at)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        })()}
                        {expandedTimelineId === b.id && (
                          <div className="mt-1.5 rounded-sm border border-border bg-secondary/30 p-1.5 text-[10.5px]"
                               data-testid={`business-booking-timeline-${b.id}`}>
                            <div className="mb-1 font-semibold uppercase tracking-wider text-muted-foreground text-[9.5px]">
                              Status timeline
                            </div>
                            {(b.status_history || []).map((h, i) => {
                              // iter55i — Duration delta from the prior step (or booking creation).
                              const prevAt = i === 0
                                ? (b.created_at || h.at)
                                : (b.status_history[i - 1]?.at || h.at);
                              let deltaText = '';
                              try {
                                const ms = new Date(h.at).getTime() - new Date(prevAt).getTime();
                                if (Number.isFinite(ms) && ms > 0) {
                                  const mins = Math.round(ms / 60000);
                                  if (mins < 60) deltaText = `${mins}m`;
                                  else if (mins < 60 * 24) deltaText = `${Math.round(mins / 60)}h`;
                                  else deltaText = `${Math.round(mins / (60 * 24))}d`;
                                }
                              } catch { /* ignore */ }
                              const priorStatus = i === 0 ? 'created' : b.status_history[i - 1]?.status;
                              return (
                                <div key={i} className="flex items-baseline gap-2"
                                     data-testid={`business-booking-timeline-step-${b.id}-${i}`}>
                                  <span className="font-mono-stat text-muted-foreground">{fmtDate(h.at)}</span>
                                  <span className="font-medium">{h.status}</span>
                                  {deltaText && (
                                    <span className="rounded-sm bg-muted px-1.5 py-0 text-[9.5px] text-muted-foreground"
                                          title={`Sat in ${priorStatus} for ${deltaText}`}>
                                      sat in {priorStatus} · {deltaText}
                                    </span>
                                  )}
                                  {h.note && <span className="italic text-muted-foreground">— {h.note}</span>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-mono-stat align-top">
                        {usd(b.amount_paid_cents || b.amount_cents)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <span className={
                          'inline-flex items-center rounded-sm px-1.5 py-0.5 text-[9.5px] uppercase tracking-wider ' +
                          (b.status === 'paid'
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
                            : b.status === 'fulfilled'
                            ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300'
                            : b.status === 'pending'
                            ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-300'
                            : 'bg-muted text-muted-foreground')
                        }>
                          {b.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {b.checkout_session_id ? (
                          <a href={`https://dashboard.stripe.com/payments/${b.receipt_url || b.checkout_session_id}`}
                             target="_blank" rel="noreferrer"
                             className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline"
                             data-testid={`business-booking-receipt-${b.id}`}>
                            <ExternalLink className="h-3 w-3" /> View
                          </a>
                        ) : <span className="text-muted-foreground">—</span>}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        <div className="pb-6 pt-2 text-center text-[10.5px] text-muted-foreground">
          Data refreshed {new Date(data.generated_at).toLocaleString()}
        </div>
      </main>
      <BusinessCenterDrawer open={businessCenterOpen} onOpenChange={setBusinessCenterOpen} />
    </div>
  );
}

/* ============================================================
 *  iter-ads1 — Amazon Ads BYO OAuth connect card.
 *  Renders three states: not-configured (platform-wide creds missing),
 *  not-connected (author hasn't linked their Amazon account), and
 *  connected (author linked — shows spend / impressions from
 *  /marketing/amazon/report).
 * ============================================================ */
function AmazonAdsConnectCard() {
  const [conns, setConns]   = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy]     = useState(false);

  const reload = async () => {
    try {
      const [c, r] = await Promise.all([
        axios.get(`${API}/marketing/connections`).then((x) => x.data),
        axios.get(`${API}/marketing/amazon/report`).then((x) => x.data),
      ]);
      setConns(c); setReport(r);
    } catch { setConns({ connections: [], amazon_ads_configured: false }); }
  };
  useEffect(() => { reload(); }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/marketing/connections/amazon/start`,
                                  { return_url: '/app/business' });
      if (!r.data?.configured) {
        toast.info('Amazon Ads OAuth not configured yet', {
          description: r.data?.hint || 'Ask your admin to set the LWA credentials.',
          duration: 8000,
        });
        return;
      }
      window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start OAuth flow.');
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Amazon Ads? Historical spend will still show.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/marketing/connections/amazon_ads`);
      toast.success('Amazon Ads disconnected.');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Disconnect failed.'); }
    finally { setBusy(false); }
  };

  if (!conns) return null;
  const connected = (conns.connections || []).some((c) => c.platform_id === 'amazon_ads' && c.status === 'connected');

  return (
    <Section
      title="Amazon Ads"
      subtitle="Link your Amazon Advertising account to pull real impressions, spend, and ACOS into this dashboard."
    >
      <Card className="p-4" data-testid="amazon-ads-connect-card">
        {connected ? (
          <div className="grid gap-3 sm:grid-cols-4">
            <KpiMini label="Impressions" value={(report?.impressions || 0).toLocaleString()} />
            <KpiMini label="Clicks"      value={(report?.clicks || 0).toLocaleString()} />
            <KpiMini label="Spend"       value={usd(report?.spend_cents || 0)} />
            <KpiMini label="Sales attrib." value={usd(report?.sales_cents || 0)} />
            {report?.report_source === 'bookings-only' && (
              <div className="sm:col-span-4 rounded-sm border border-amber-500/40 bg-amber-500/5 p-2 text-[11px] text-amber-800 dark:text-amber-300">
                <AlertTriangle className="mr-1 inline h-3 w-3" />
                {report.note}
              </div>
            )}
            <div className="sm:col-span-4 flex justify-end">
              <Button variant="ghost" size="sm" onClick={disconnect} disabled={busy}
                      className="h-7 gap-1 text-[10.5px] text-rose-600"
                      data-testid="amazon-ads-disconnect">
                <Unlink className="h-3 w-3" /> Disconnect
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <div className="text-[13px] font-semibold">Not connected</div>
              <div className="text-[11.5px] text-muted-foreground">
                {conns.amazon_ads_configured
                  ? 'Sign in with your Amazon Advertising account. We\u2019ll pull impressions, spend, and ACOS in read-only mode.'
                  : 'Amazon\u2019s LWA credentials are not set on this deployment yet — bookings still run through the managed flow.'}
              </div>
            </div>
            <Button size="sm" onClick={connect} disabled={busy}
                    className="h-8 gap-1 text-[11.5px]"
                    data-testid="amazon-ads-connect">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Link2 className="h-3 w-3" />}
              Connect Amazon Ads
            </Button>
            <HelpTip
              slug="amazon-ads-sync"
              title="Amazon Ads sync"
              content={<>Read-only OAuth to your Amazon Advertising account. We pull <b>impressions, spend, and ACOS</b> nightly — never create campaigns. Live pulls activate once your admin sets <code>AMAZON_ADS_LIVE_ENABLED=true</code> and the Ads-read scope is approved.</>}
            >
              <span data-testid="amazon-ads-help-anchor" className="sr-only">amazon-ads-help</span>
            </HelpTip>
          </div>
        )}
      </Card>
    </Section>
  );
}

/* ============================================================
 *  iter-ads4 — Meta Ads BYO OAuth connect card.
 *  Same three-state UX as Amazon Ads. Mounted just below it on the
 *  Business Dashboard.
 * ============================================================ */
function MetaAdsConnectCard() {
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy]     = useState(false);

  const reload = async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/marketing/meta/status`).then((x) => x.data),
        axios.get(`${API}/marketing/meta/report`).then((x) => x.data),
      ]);
      setStatus(s); setReport(r);
    } catch { setStatus({ configured: false, connected: false }); }
  };
  useEffect(() => { reload(); }, []);

  // Handle the OAuth redirect coming back to /app/business with
  // ?meta_code + ?meta_state (frontend rewrites Meta's ?code+?state
  // to these before this component mounts — see below).
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const code  = p.get('meta_code');
    const state = p.get('meta_state');
    if (!code || !state) return;
    (async () => {
      setBusy(true);
      try {
        await axios.post(`${API}/marketing/connections/meta/callback`, { code, state });
        toast.success('Meta Ads connected.');
        // Strip the params so a reload doesn't retry the exchange.
        p.delete('meta_code'); p.delete('meta_state');
        const clean = `${window.location.pathname}${p.toString() ? '?' + p.toString() : ''}`;
        window.history.replaceState({}, '', clean);
        await reload();
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Meta callback failed.');
      } finally { setBusy(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/marketing/connections/meta/start`,
                                  { return_url: '/app/business' });
      if (!r.data?.configured) {
        toast.info('Meta Ads OAuth not configured yet', {
          description: r.data?.hint || 'Ask your admin to set the Meta app credentials.',
          duration: 8000,
        });
        return;
      }
      window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start OAuth flow.');
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect Meta Ads? Historical spend will still show.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/marketing/connections/meta_ads`);
      toast.success('Meta Ads disconnected.');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Disconnect failed.'); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  const connected = !!status.connected;
  const pending   = report?.report_source === 'pending_live';

  return (
    <Section
      title="Meta Ads"
      subtitle="Link your Meta Ads account to pull real impressions, spend, and CPC into this dashboard. Read-only — we never create campaigns."
    >
      <Card className="p-4" data-testid="meta-ads-connect-card">
        {connected ? (
          <div className="grid gap-3 sm:grid-cols-4">
            <KpiMini label="Impressions" value={(report?.impressions || 0).toLocaleString()} />
            <KpiMini label="Clicks"      value={(report?.clicks || 0).toLocaleString()} />
            <KpiMini label="Spend"       value={usd(report?.spend_cents || 0)} />
            <KpiMini label="CPC"         value={report?.cpc ? `$${Number(report.cpc).toFixed(2)}` : '—'} />
            {pending && (
              <div className="sm:col-span-4 rounded-sm border border-amber-500/40 bg-amber-500/5 p-2 text-[11px] text-amber-800 dark:text-amber-300"
                   data-testid="meta-ads-pending-live">
                <AlertTriangle className="mr-1 inline h-3 w-3" />
                {report.note}
              </div>
            )}
            {report?.report_source === 'meta_ads_api_error' && (
              <div className="sm:col-span-4 rounded-sm border border-rose-500/40 bg-rose-500/5 p-2 text-[11px] text-rose-800 dark:text-rose-300"
                   data-testid="meta-ads-error">
                <AlertTriangle className="mr-1 inline h-3 w-3" />
                {report.note}
              </div>
            )}
            <div className="sm:col-span-4 flex justify-end">
              <Button variant="ghost" size="sm" onClick={disconnect} disabled={busy}
                      className="h-7 gap-1 text-[10.5px] text-rose-600"
                      data-testid="meta-ads-disconnect">
                <Unlink className="h-3 w-3" /> Disconnect
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <div className="text-[13px] font-semibold">Not connected</div>
              <div className="text-[11.5px] text-muted-foreground">
                {status.configured
                  ? 'Sign in with your Meta ad account. We\u2019ll pull impressions, clicks, and spend in read-only mode.'
                  : 'Meta app credentials aren\u2019t set on this deployment yet — bookings still run through the managed flow.'}
              </div>
            </div>
            <Button size="sm" onClick={connect} disabled={busy}
                    className="h-8 gap-1 text-[11.5px]"
                    data-testid="meta-ads-connect">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Facebook className="h-3 w-3" />}
              Connect Meta Ads
            </Button>
            <HelpTip
              slug="meta-ads-sync"
              title="Meta Ads sync"
              content={<>Read-only OAuth (<code>ads_read</code>) to your Meta ad account. Nightly Graph API v26 pull of <b>impressions, clicks, spend, and CPC</b>. Never creates campaigns. Live pulls activate once your admin sets <code>META_ADS_LIVE_ENABLED=true</code> after Meta App Review approves the scope.</>}
            >
              <span data-testid="meta-ads-help-anchor" className="sr-only">meta-ads-help</span>
            </HelpTip>
          </div>
        )}
      </Card>
    </Section>
  );
}

/* ============================================================
 *  iter-p2-h — "Which Ad Platform Is Winning" comparison tile
 *  + "Marketing ROI Overview" blended-ROAS card.
 *  Both fed by GET /api/marketing/ads-summary in a single call.
 * ============================================================ */

const PLATFORM_ICON = {
  amazon_ads: ShoppingBag,
  meta_ads:   Facebook,
  tiktok_ads: Video,
};

function AdsSummarySection() {
  const [data, setData] = useState(null);
  const [err,  setErr]  = useState(false);
  useEffect(() => {
    axios.get(`${API}/marketing/ads-summary`)
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, []);
  if (err)  return null;   // Silent fallback — every card on this page is optional.
  if (!data) return null;

  const anyConnected = (data.totals?.connected_count || 0) > 0;
  if (!anyConnected) {
    // No connections yet — hide both tiles rather than show
    // three empty rows. Once the author connects any platform the
    // section reappears.
    return null;
  }

  return (
    <>
      <AdPlatformComparisonTile data={data} />
      <MarketingRoiOverviewCard  data={data} />
    </>
  );
}

function AdPlatformComparisonTile({ data }) {
  const rows = data.rows || [];
  const winner = data.winner;
  const usdFine = (c) => `$${((Number(c) || 0) / 100).toFixed(2)}`;
  const pct = (v) => (v == null ? '—' : `${v.toFixed(2)}%`);
  const cpc = (v) => (v == null ? '—' : `$${v.toFixed(2)}`);
  return (
    <Section
      title="Which ad platform is winning"
      subtitle="Last 30 days side-by-side. Winner = the lowest CPC among platforms with real clicks. Rows show 'pending live' until each platform's live-sync flag is flipped by your admin."
    >
      <Card className="p-0 overflow-hidden" data-testid="ads-summary-tile">
        <table className="w-full text-[12px]" data-testid="ads-summary-table">
          <thead className="bg-panel-header/40">
            <tr className="text-left text-[10.5px] uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2">Platform</th>
              <th className="px-3 py-2 text-right">Impressions</th>
              <th className="px-3 py-2 text-right">Clicks</th>
              <th className="px-3 py-2 text-right">Spend</th>
              <th className="px-3 py-2 text-right">CPC</th>
              <th className="px-3 py-2 text-right">Conv. rate</th>
              <th className="px-3 py-2 w-24">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const Icon = PLATFORM_ICON[r.platform_id] || ShoppingBag;
              const isWinner = r.platform_id === winner;
              return (
                <tr key={r.platform_id}
                    className={'border-t border-border '
                      + (isWinner ? 'bg-amber-500/5' : '')}
                    data-testid={`ads-summary-row-${r.platform_id}`}>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1.5 font-semibold">
                      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                      {r.platform_name}
                      {isWinner && (
                        <span className="inline-flex items-center gap-0.5 rounded-sm bg-amber-500/15 px-1.5 py-0.5 text-[9.5px] font-bold uppercase tracking-wider text-amber-800 dark:text-amber-300"
                              data-testid={`ads-summary-winner-badge-${r.platform_id}`}>
                          <Trophy className="h-2.5 w-2.5" /> Winner
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono-stat">
                    {(r.impressions || 0).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-stat">
                    {(r.clicks || 0).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-stat">
                    {usdFine(r.spend_cents)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-stat">
                    {cpc(r.cpc)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-stat">
                    {pct(r.conversion_rate_pct)}
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill source={r.report_source} connected={r.connected} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </Section>
  );
}

function StatusPill({ source, connected }) {
  const label = !connected ? 'Not connected'
    : source === 'pending_live'         ? 'Pending live'
    : source === 'not_connected'        ? 'Not connected'
    : source === 'amazon_ads_api_error' ? 'API error'
    : source === 'meta_ads_api_error'   ? 'API error'
    : source === 'tiktok_ads_api_error' ? 'API error'
    : source === 'bookings-only'        ? 'Bookings only'
    : 'Live';
  const tone = !connected || source === 'not_connected'
    ? 'bg-muted text-muted-foreground'
    : source === 'pending_live'
    ? 'bg-amber-500/10 text-amber-800 dark:text-amber-300'
    : /error/i.test(source)
    ? 'bg-rose-500/10 text-rose-800 dark:text-rose-300'
    : source === 'bookings-only'
    ? 'bg-sky-500/10 text-sky-800 dark:text-sky-300'
    : 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300';
  return (
    <span className={'inline-flex items-center rounded-sm px-1.5 py-0.5 text-[10.5px] font-semibold ' + tone}>
      {label}
    </span>
  );
}

function MarketingRoiOverviewCard({ data }) {
  const roi = data.roi || {};
  const [pausing, setPausing] = useState(false);
  const [paused, setPaused]   = useState(null);      // last pause receipt
  const usdFine = (c) => `$${((Number(c) || 0) / 100).toFixed(2)}`;
  const roas = roi.blended_roas;
  const worst = data.worst;
  const roasTone = roas == null
    ? 'text-muted-foreground'
    : roas >= 3   ? 'text-emerald-600 dark:text-emerald-400'
    : roas >= 1   ? 'text-amber-700 dark:text-amber-300'
    :                'text-rose-600 dark:text-rose-400';
  const verdict = roas == null
    ? 'Spend real dollars via a connected platform to see blended ROAS here.'
    : roas >= 3 ? 'Healthy — every $1 of ad spend is bringing back more than $3 in royalties.'
    : roas >= 1 ? 'Break-even zone — ads are paying for themselves but not much more. Try lowering CPC on the winner.'
    :             'Below break-even. Consider pausing the worst-CPC platform in the tile above and reallocating.';

  const belowBreakEven = roas != null && roas < 1;
  const canAutoPause   = belowBreakEven && !!worst;

  const autoPause = async () => {
    if (pausing || !canAutoPause) return;
    const worstLabel = (data.rows || []).find((r) => r.platform_id === worst)?.platform_name || 'the worst-CPC platform';
    if (!window.confirm(`Pause every active opt-in on ${worstLabel}? Live insights keep syncing so you can see the impact.`)) {
      return;
    }
    setPausing(true);
    try {
      const r = await axios.post(`${API}/marketing/ads-summary/auto-pause-worst`, {});
      setPaused(r.data);
      toast.success(r.data?.note || `Paused ${r.data?.platform_name}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-pause failed.');
    } finally { setPausing(false); }
  };

  return (
    <Section
      title="Marketing ROI overview"
      subtitle="Blended ROAS across every connected platform vs. your last 30 days of book royalties."
    >
      <Card className="p-4" data-testid="marketing-roi-card">
        <div className="grid gap-3 sm:grid-cols-3">
          <KpiMini label="Ad spend · 30d" value={usdFine(roi.spend_cents_30d)} />
          <KpiMini label="Royalties · 30d" value={usdFine(roi.earnings_cents_30d)} />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Blended ROAS
            </div>
            <div className={`flex items-center gap-1 font-mono-stat text-[15px] font-semibold ${roasTone}`}
                 data-testid="marketing-roi-blended-roas">
              <Target className="h-3.5 w-3.5" />
              {roas == null ? '—' : `${roas.toFixed(2)}×`}
            </div>
          </div>
        </div>
        <p className="mt-2 text-[11.5px] text-muted-foreground" data-testid="marketing-roi-verdict">
          {verdict}
        </p>
        {/* iter-p2-i — Auto-pause action.  Only surfaces when ROAS is
            below break-even AND we have a `worst` pick with real
            click data.  A confirm() gate prevents accidental
            wallet-emptying clicks; the receipt renders in-place. */}
        {canAutoPause && !paused && (
          <div className="mt-3 flex items-center justify-between gap-2 rounded-md border border-rose-500/40 bg-rose-500/5 p-2">
            <div className="text-[11.5px] text-rose-800 dark:text-rose-300">
              <AlertTriangle className="mr-1 inline h-3 w-3" />
              Auto-pause the worst-CPC platform to stop the bleed?
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={autoPause}
              disabled={pausing}
              className="h-7 gap-1 border-rose-500/50 text-[11.5px] text-rose-700 hover:bg-rose-500/10"
              data-testid="marketing-roi-auto-pause"
            >
              {pausing ? <Loader2 className="h-3 w-3 animate-spin" /> : <XIcon className="h-3 w-3" />}
              Pause {(data.rows || []).find((r) => r.platform_id === worst)?.platform_name || 'worst'}
            </Button>
          </div>
        )}
        {paused && (
          <div className="mt-3 rounded-md border border-emerald-500/40 bg-emerald-500/5 p-2 text-[11.5px] text-emerald-800 dark:text-emerald-300"
               data-testid="marketing-roi-auto-pause-receipt">
            <BadgeCheck className="mr-1 inline h-3 w-3" />
            {paused.note}
          </div>
        )}
      </Card>
    </Section>
  );
}

function KpiMini({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-mono-stat text-[15px] font-semibold">{value}</div>
    </div>
  );
}

/* ============================================================
 *  iter-ads5 — TikTok Ads BYO OAuth connect card.
 *  TikTok tokens are long-term (no timer), so the "needs reauth"
 *  branch only fires when the API returns a revoked-token error.
 * ============================================================ */
function TikTokAdsConnectCard() {
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy]     = useState(false);

  const reload = async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/marketing/tiktok/status`).then((x) => x.data),
        axios.get(`${API}/marketing/tiktok/report`).then((x) => x.data),
      ]);
      setStatus(s); setReport(r);
    } catch { setStatus({ configured: false, connected: false }); }
  };
  useEffect(() => { reload(); }, []);

  // Handle the OAuth redirect. TikTok appends `?auth_code=` + `state=`,
  // but the frontend rewrites those to `?tiktok_auth_code=` +
  // `?tiktok_state=` so multiple providers can share the same
  // /app/business landing URL without stepping on each other.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const auth_code = p.get('tiktok_auth_code') || p.get('auth_code');
    const state     = p.get('tiktok_state')     || p.get('state');
    // Only claim the params if TikTok is the provider that just
    // redirected (Meta's callback uses `code=`, not `auth_code=`).
    if (!auth_code || !state) return;
    (async () => {
      setBusy(true);
      try {
        await axios.post(`${API}/marketing/connections/tiktok/callback`, { auth_code, state });
        toast.success('TikTok Ads connected.');
        p.delete('tiktok_auth_code'); p.delete('tiktok_state');
        p.delete('auth_code');        p.delete('state');
        const clean = `${window.location.pathname}${p.toString() ? '?' + p.toString() : ''}`;
        window.history.replaceState({}, '', clean);
        await reload();
      } catch (e) {
        // Silent failure if state doesn't match — this may not be
        // our callback at all (state mismatch already handled by
        // Meta callback earlier in the tree).
        if (e?.response?.status !== 400) {
          toast.error(e?.response?.data?.detail || 'TikTok callback failed.');
        }
      } finally { setBusy(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/marketing/connections/tiktok/start`,
                                  { return_url: '/app/business' });
      if (!r.data?.configured) {
        toast.info('TikTok Ads OAuth not configured yet', {
          description: r.data?.hint || 'Ask your admin to set the TikTok app credentials.',
          duration: 8000,
        });
        return;
      }
      window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start OAuth flow.');
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect TikTok Ads? Historical spend will still show.')) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/marketing/connections/tiktok_ads`);
      toast.success('TikTok Ads disconnected.');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Disconnect failed.'); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  const connected = !!status.connected;
  const pending   = report?.report_source === 'pending_live';

  return (
    <Section
      title="TikTok Ads"
      subtitle="Link your TikTok Ads Manager account to pull real impressions, spend, and conversions into this dashboard. Read-only — we never create campaigns."
    >
      <Card className="p-4" data-testid="tiktok-ads-connect-card">
        {connected ? (
          <div className="grid gap-3 sm:grid-cols-4">
            <KpiMini label="Impressions" value={(report?.impressions || 0).toLocaleString()} />
            <KpiMini label="Clicks"      value={(report?.clicks || 0).toLocaleString()} />
            <KpiMini label="Spend"       value={usd(report?.spend_cents || 0)} />
            <KpiMini label="Conversions" value={(report?.conversions || 0).toLocaleString()} />
            {pending && (
              <div className="sm:col-span-4 rounded-sm border border-amber-500/40 bg-amber-500/5 p-2 text-[11px] text-amber-800 dark:text-amber-300"
                   data-testid="tiktok-ads-pending-live">
                <AlertTriangle className="mr-1 inline h-3 w-3" />
                {report.note}
              </div>
            )}
            {report?.report_source === 'tiktok_ads_api_error' && (
              <div className="sm:col-span-4 rounded-sm border border-rose-500/40 bg-rose-500/5 p-2 text-[11px] text-rose-800 dark:text-rose-300"
                   data-testid="tiktok-ads-error">
                <AlertTriangle className="mr-1 inline h-3 w-3" />
                {report.note}
              </div>
            )}
            <div className="sm:col-span-4 flex justify-end">
              <Button variant="ghost" size="sm" onClick={disconnect} disabled={busy}
                      className="h-7 gap-1 text-[10.5px] text-rose-600"
                      data-testid="tiktok-ads-disconnect">
                <Unlink className="h-3 w-3" /> Disconnect
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <div className="text-[13px] font-semibold">Not connected</div>
              <div className="text-[11.5px] text-muted-foreground">
                {status.configured
                  ? 'Sign in with your TikTok Ads Manager account. We\u2019ll pull impressions, clicks, spend, and conversions in read-only mode.'
                  : 'TikTok app credentials aren\u2019t set on this deployment yet — bookings still run through the managed flow.'}
              </div>
            </div>
            <Button size="sm" onClick={connect} disabled={busy}
                    className="h-8 gap-1 text-[11.5px]"
                    data-testid="tiktok-ads-connect">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Video className="h-3 w-3" />}
              Connect TikTok Ads
            </Button>
            <HelpTip
              slug="tiktok-ads-sync"
              title="TikTok Ads sync"
              content={<>Read-only OAuth to your TikTok Ads Manager account. Nightly TikTok Business API v1.3 pull of <b>impressions, clicks, spend, and conversions</b> (AUCTION advertiser-level). Never creates campaigns. Live pulls activate once your admin sets <code>TIKTOK_ADS_LIVE_ENABLED=true</code> after App Review approves reporting access.</>}
            >
              <span data-testid="tiktok-ads-help-anchor" className="sr-only">tiktok-ads-help</span>
            </HelpTip>
          </div>
        )}
      </Card>
    </Section>
  );
}

/* ============================================================
 *  iter-shelf2 — Click Analyzer.
 *  Per-book funnel table (views → sample opens → buy clicks → sales)
 *  with a plain-English diagnosis of where readers are leaking. Uses
 *  book_events (populated by storefront telemetry) + book_sales_ledger.
 * ============================================================ */
function ClickAnalyzer() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/authors/me/bookshelf/click-analyzer?days=30`)
      .then((r) => setData(r.data))
      .catch(() => setData({ days: 30, rows: [] }));
  }, []);
  if (!data) return null;
  return (
    <Section
      title="Click analyzer"
      subtitle="Last 30 days of storefront funnel per Live book. Diagnoses where readers drop off so you know what to polish."
    >
      {data.rows.length === 0 ? (
        <Card className="p-6 text-center text-[12px] text-muted-foreground"
              data-testid="click-analyzer-empty">
          No published books yet — publish a manuscript from My Bookshelf to start collecting funnel data.
        </Card>
      ) : (
        <div className="overflow-hidden rounded-md border border-border">
          <table className="w-full text-[12px]" data-testid="click-analyzer-table">
            <thead className="bg-panel-header/40">
              <tr className="text-left text-[10.5px] uppercase tracking-wider text-muted-foreground">
                <th className="px-3 py-2">Book</th>
                <th className="px-3 py-2 text-right">Views</th>
                <th className="px-3 py-2 text-right">Sample opens</th>
                <th className="px-3 py-2 text-right">Buy clicks</th>
                <th className="px-3 py-2 text-right">Sales</th>
                <th className="px-3 py-2 text-right">CVR</th>
                <th className="px-3 py-2">Diagnosis</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const isLeak = /polish|friction|hook|leak|discovery|engagement/i.test(r.diagnosis || '');
                return (
                <tr key={r.book_id} className="border-t border-border"
                    data-testid={`click-analyzer-row-${r.book_id}`}>
                  <td className="px-3 py-2 font-medium">{r.title}</td>
                  <td className="px-3 py-2 text-right font-mono-stat">{r.views.toLocaleString()}</td>
                  <td className="px-3 py-2 text-right font-mono-stat">{r.sample_opens}</td>
                  <td className="px-3 py-2 text-right font-mono-stat">{r.buy_clicks}</td>
                  <td className="px-3 py-2 text-right font-mono-stat">{r.sales}</td>
                  <td className="px-3 py-2 text-right font-mono-stat">
                    {r.cvr_pct.toFixed(2)}%
                  </td>
                  <td className="px-3 py-2">
                    <span className={
                      'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[10.5px] ' +
                      (isLeak
                        ? 'bg-amber-500/10 text-amber-800 dark:text-amber-300'
                        : r.diagnosis === 'healthy'
                        ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                        : 'bg-muted text-muted-foreground')
                    }>
                      {isLeak ? <TrendingDown className="h-3 w-3" /> : <Zap className="h-3 w-3" />}
                      {r.diagnosis}
                    </span>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  );
}

/* ============================================================
 *  iter-p2-n — Reader Journey Analytics.
 *  Chapter-by-chapter drop-off funnel + sample→buy conversion.
 * ============================================================ */
function ReaderJourneyAnalyticsCard() {
  const [books, setBooks] = useState([]);
  const [selBook, setSelBook] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // iter-p2-o — Cliffhanger AI state
  const [cliffhanger, setCliffhanger] = useState(null);
  const [cliffLoading, setCliffLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/reader-magnet/author/list`)
      .then((r) => setBooks(r.data?.items || []))
      .catch(() => setBooks([]));
  }, []);

  const load = async (bookId) => {
    setSelBook(bookId);
    setLoading(true);
    setCliffhanger(null);
    try {
      const r = await axios.get(`${API}/analytics/reader-journey/${bookId}`);
      setData(r.data);
    } catch { setData(null); }
    finally { setLoading(false); }
  };

  const runCliffhanger = async () => {
    if (!selBook || cliffLoading) return;
    setCliffLoading(true);
    setCliffhanger(null);
    try {
      const r = await axios.post(`${API}/analytics/cliffhanger/${selBook}`,
                                  { tail_words: 350 });
      setCliffhanger(r.data);
      toast.success(`Rewrote the tail of chapter ${r.data.chapter.number}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not run cliffhanger AI.');
    } finally { setCliffLoading(false); }
  };

  if (books.length === 0) return null;
  return (
    <Section title="Reader Journey"
             subtitle="Chapter-by-chapter drop-off + sample-to-buy conversion for the Reader Magnet.">
      <div className="flex flex-wrap gap-1.5" data-testid="rja-book-list">
        {books.map((b) => (
          <button key={b.book_id} type="button"
                  onClick={() => load(b.book_id)}
                  className={'rounded-md border px-2.5 py-1 text-[11.5px] transition ' +
                    (selBook === b.book_id
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border bg-card hover:border-primary/40')}
                  data-testid={`rja-book-btn-${b.book_id}`}>
            {b.title}
          </button>
        ))}
      </div>
      {loading && <div className="mt-3 text-[12px] text-muted-foreground">Loading&hellip;</div>}
      {data && (
        <div className="mt-4 space-y-4" data-testid="rja-panel">
          <div className="grid gap-3 sm:grid-cols-3">
            <Card className="p-3">
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Total buyers</div>
              <div className="mt-0.5 font-mono-stat text-[18px] font-semibold">{data.total_buyers}</div>
            </Card>
            <Card className="p-3">
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Sample opt-ins</div>
              <div className="mt-0.5 font-mono-stat text-[18px] font-semibold">{data.reader_magnet.captured}</div>
              <div className="text-[10.5px] text-muted-foreground">
                {data.reader_magnet.enabled ? 'Magnet ON' : 'Magnet OFF'}
              </div>
            </Card>
            <Card className="p-3">
              <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">Conversion</div>
              <div className="mt-0.5 font-mono-stat text-[18px] font-semibold">
                {data.reader_magnet.conversion_pct.toFixed(1)}%
              </div>
              <div className="text-[10.5px] text-muted-foreground">
                {data.reader_magnet.converted} of {data.reader_magnet.captured}
              </div>
            </Card>
          </div>
          {/* iter-p2-o — Cliffhanger AI trigger */}
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-primary/40 bg-primary/5 p-3"
               data-testid="rja-cliffhanger-cta">
            <Zap className="h-4 w-4 text-primary" />
            <div className="flex-1 text-[12px]">
              <b>Cliffhanger AI</b>{' '}
              <span className="text-muted-foreground">
                — Claude rewrites the last page of your highest-drop chapter as a stronger hook.
              </span>
            </div>
            <Button size="sm" onClick={runCliffhanger} disabled={cliffLoading}
                    className="h-8 gap-1 text-[11.5px]"
                    data-testid="rja-cliffhanger-run">
              {cliffLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
              Rewrite highest-drop chapter
            </Button>
          </div>
          {cliffhanger && (
            <Card className="p-4" data-testid="rja-cliffhanger-result">
              <div className="text-[10.5px] uppercase tracking-wider text-primary">
                Chapter {cliffhanger.chapter.number}: {cliffhanger.chapter.title}
              </div>
              <p className="mt-1 text-[12px] text-muted-foreground"
                 data-testid="rja-cliffhanger-diagnosis">
                {cliffhanger.diagnosis}
              </p>
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                <div>
                  <div className="mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">Before</div>
                  <div className="max-h-64 overflow-auto rounded-md border border-border bg-panel-header/30 p-3 text-[12px] leading-relaxed"
                       style={{ fontFamily: 'Georgia, serif' }}
                       data-testid="rja-cliffhanger-before">
                    {cliffhanger.before}
                  </div>
                </div>
                <div>
                  <div className="mb-1 text-[10.5px] uppercase tracking-wider text-primary">After (Claude · editable)</div>
                  <textarea
                    value={cliffhanger.after}
                    onChange={(e) => setCliffhanger((c) => ({ ...c, after: e.target.value }))}
                    className="h-64 w-full resize-none overflow-auto rounded-md border border-primary/40 bg-primary/5 p-3 text-[12px] leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary/40"
                    style={{ fontFamily: 'Georgia, serif' }}
                    data-testid="rja-cliffhanger-after"
                  />
                </div>
              </div>
              {/* iter-p2-r — Paired chapter N+1 opener rewrite */}
              {cliffhanger.opener && (
                <div className="mt-4 rounded-md border border-primary/30 bg-primary/[0.03] p-3"
                     data-testid="rja-opener-block">
                  <div className="text-[10.5px] uppercase tracking-wider text-primary">
                    Paired opener — Chapter {cliffhanger.opener.chapter.number}: {cliffhanger.opener.chapter.title}
                  </div>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    A magnetic first paragraph for the next chapter so the cliffhanger actually pays off.
                  </p>
                  <div className="mt-2 grid gap-3 lg:grid-cols-2">
                    <div>
                      <div className="mb-1 text-[10.5px] uppercase tracking-wider text-muted-foreground">Before</div>
                      <div className="max-h-48 overflow-auto rounded-md border border-border bg-panel-header/30 p-3 text-[12px] leading-relaxed"
                           style={{ fontFamily: 'Georgia, serif' }}
                           data-testid="rja-opener-before">
                        {cliffhanger.opener.before}
                      </div>
                    </div>
                    <div>
                      <div className="mb-1 text-[10.5px] uppercase tracking-wider text-primary">After (Claude · editable)</div>
                      <textarea
                        value={cliffhanger.opener.after}
                        onChange={(e) => setCliffhanger((c) => ({ ...c,
                          opener: { ...c.opener, after: e.target.value } }))}
                        className="h-48 w-full resize-none overflow-auto rounded-md border border-primary/40 bg-primary/5 p-3 text-[12px] leading-relaxed focus:outline-none focus:ring-1 focus:ring-primary/40"
                        style={{ fontFamily: 'Georgia, serif' }}
                        data-testid="rja-opener-after"
                      />
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Button size="sm"
                            onClick={async () => {
                              setCliffLoading(true);
                              try {
                                const r = await axios.post(
                                  `${API}/analytics/cliffhanger/${selBook}/apply-opener`,
                                  { chapter_number: cliffhanger.opener.chapter.number,
                                    new_head_text: cliffhanger.opener.after,
                                    head_words: cliffhanger.opener.head_words });
                                toast.success(`Applied opener to chapter ${r.data.chapter}. Rollback snapshot saved.`);
                                setCliffhanger((c) => ({ ...c, opener: null }));
                              } catch (e) {
                                toast.error(e?.response?.data?.detail || 'Apply failed.');
                              } finally { setCliffLoading(false); }
                            }}
                            disabled={cliffLoading || !cliffhanger.opener.after?.trim()}
                            className="h-8 gap-1 text-[11.5px]"
                            data-testid="rja-opener-apply">
                      {cliffLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <BadgeCheck className="h-3 w-3" />}
                      Apply opener
                    </Button>
                  </div>
                </div>
              )}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button size="sm"
                        onClick={async () => {
                          setCliffLoading(true);
                          try {
                            const r = await axios.post(
                              `${API}/analytics/cliffhanger/${selBook}/apply`,
                              { chapter_number: cliffhanger.chapter.number,
                                new_tail_text: cliffhanger.after,
                                tail_words: cliffhanger.tail_words });
                            toast.success(`Applied to chapter ${r.data.chapter}. Previous version snapshotted for rollback.`);
                            setCliffhanger(null);
                          } catch (e) {
                            toast.error(e?.response?.data?.detail || 'Apply failed.');
                          } finally { setCliffLoading(false); }
                        }}
                        disabled={cliffLoading || !cliffhanger.after?.trim()}
                        className="h-8 gap-1 text-[11.5px]"
                        data-testid="rja-cliffhanger-apply">
                  {cliffLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <BadgeCheck className="h-3 w-3" />}
                  Apply to manuscript
                </Button>
                <Button size="sm" variant="ghost"
                        onClick={() => setCliffhanger(null)}
                        className="h-8 text-[11.5px] text-muted-foreground"
                        data-testid="rja-cliffhanger-discard">
                  Discard
                </Button>
                <div className="text-[10.5px] text-muted-foreground">
                  Edit the right pane if you like — Apply writes exactly what you see. Prior text is snapshotted for rollback.
                </div>
              </div>
            </Card>
          )}
          <div className="overflow-hidden rounded-md border border-border">
            <table className="w-full text-[12px]" data-testid="rja-funnel-table">
              <thead className="bg-panel-header/40">
                <tr className="text-left text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2">Chapter</th>
                  <th className="px-3 py-2 text-right">Readers</th>
                  <th className="px-3 py-2 text-right">Reach %</th>
                  <th className="px-3 py-2 text-right">Drop to next</th>
                </tr>
              </thead>
              <tbody>
                {data.chapter_funnel.map((c) => (
                  <tr key={c.chapter} className="border-t border-border"
                      data-testid={`rja-row-${c.chapter}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium">Ch {c.chapter}</div>
                      <div className="truncate text-[10.5px] text-muted-foreground">{c.title}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono-stat">{c.readers}</td>
                    <td className="px-3 py-2 text-right font-mono-stat">{c.reach_pct.toFixed(1)}%</td>
                    <td className={'px-3 py-2 text-right font-mono-stat ' +
                        (c.drop_pct >= 40 ? 'text-rose-600'
                          : c.drop_pct >= 20 ? 'text-amber-600' : 'text-muted-foreground')}>
                      {c.drop_to_next != null
                        ? `${c.drop_to_next} (${c.drop_pct.toFixed(1)}%)`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Section>
  );
}

/* ============================================================
 *  iter-p2-n — Series Boxed-Set Auto-Bundler.
 * ============================================================ */
function SeriesBundlerCard() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState('');

  const reload = () => {
    axios.get(`${API}/bookstore/bundles/series-eligible`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  };
  useEffect(() => { reload(); }, []);

  const create = async (slug) => {
    setBusy(slug);
    try {
      const r = await axios.post(`${API}/bookstore/bundles/auto-create/${slug}`,
                                  { discount_pct: 20 });
      if (r.data?.created) {
        toast.success(`Boxed set "${r.data.bundle.title}" is live.`);
      } else {
        toast.success('That series already has a bundle.');
      }
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create bundle.');
    } finally { setBusy(''); }
  };

  if (!items || items.length === 0) return null;
  return (
    <Section title="Series Boxed-Set Bundler"
             subtitle="You've published 3+ books in a series — one tap wraps them into a discounted boxed set.">
      <div className="grid gap-3 sm:grid-cols-2" data-testid="series-bundler-grid">
        {items.map((it) => (
          <Card key={it.series_slug} className="p-4"
                data-testid={`series-item-${it.series_slug}`}>
            <div className="text-[10.5px] uppercase tracking-wider text-primary">
              {it.book_count} books
            </div>
            <div className="text-[14px] font-semibold">{it.series_name}</div>
            <div className="mt-2 flex flex-wrap gap-1 text-[10.5px] text-muted-foreground">
              {it.books.slice(0, 6).map((b) => (
                <span key={b.id} className="rounded-sm border border-border px-1 py-0.5">
                  {b.title}
                </span>
              ))}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
              <div>
                <div className="text-muted-foreground">Regular</div>
                <div className="font-mono-stat">{usd(it.regular_price_cents)}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Bundle ({it.proposed_discount_pct}% off)</div>
                <div className="font-mono-stat text-primary">{usd(it.proposed_price_cents)}</div>
              </div>
            </div>
            {it.already_bundled ? (
              <div className="mt-3 flex items-center gap-1 text-[11.5px] text-emerald-700 dark:text-emerald-400">
                <BadgeCheck className="h-3.5 w-3.5" /> Bundle live
              </div>
            ) : (
              <Button size="sm"
                      className="mt-3 h-8 w-full gap-1.5 text-[11.5px]"
                      disabled={busy === it.series_slug}
                      onClick={() => create(it.series_slug)}
                      data-testid={`series-create-${it.series_slug}`}>
                {busy === it.series_slug ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                Auto-bundle at {it.proposed_discount_pct}% off
              </Button>
            )}
          </Card>
        ))}
      </div>
    </Section>
  );
}

/* ============================================================
 *  iter-p2-n — Reader Magnet toggle grid.
 * ============================================================ */
function ReaderMagnetToggleCard() {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState('');

  const reload = () => {
    axios.get(`${API}/reader-magnet/author/list`)
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]));
  };
  useEffect(() => { reload(); }, []);

  const toggle = async (it) => {
    setBusy(it.book_id);
    try {
      await axios.post(`${API}/reader-magnet/toggle/${it.book_id}`,
                        { enabled: !it.enabled });
      toast.success(!it.enabled
        ? `Free sample on for "${it.title}". Share /read-free/${it.slug}.`
        : `Sample off for "${it.title}".`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not toggle.');
    } finally { setBusy(''); }
  };

  if (!items || items.length === 0) return null;
  return (
    <Section title="Chapter-1 Reader Magnet"
             subtitle="Turn discovery scans into email opt-ins. Toggles the public /read-free/<slug> landing on per book.">
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="magnet-grid">
        {items.map((it) => (
          <Card key={it.book_id} className="p-3"
                data-testid={`magnet-item-${it.book_id}`}>
            <div className="mb-2 flex items-start gap-2">
              {it.cover_url ? (
                <img src={it.cover_url} alt="Cover"
                     className="h-14 w-10 flex-none rounded-sm border border-border object-cover" />
              ) : (
                <div className="flex h-14 w-10 flex-none items-center justify-center rounded-sm border border-border bg-panel-header/40">
                  <BookOpen className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12.5px] font-semibold">{it.title}</div>
                <div className="text-[10.5px] text-muted-foreground">
                  {it.captured} opt-in · {it.converted} bought
                </div>
              </div>
            </div>
            <Button size="sm"
                    variant={it.enabled ? 'default' : 'outline'}
                    className="h-7 w-full gap-1 text-[11px]"
                    disabled={busy === it.book_id}
                    onClick={() => toggle(it)}
                    data-testid={`magnet-toggle-${it.book_id}`}>
              {busy === it.book_id
                ? <Loader2 className="h-3 w-3 animate-spin" />
                : it.enabled ? <BadgeCheck className="h-3 w-3" /> : <Plus className="h-3 w-3" />}
              {it.enabled ? 'Magnet ON — view /read-free' : 'Turn magnet on'}
            </Button>
            {it.enabled && (
              <a href={`/read-free/${it.slug}`} target="_blank" rel="noreferrer"
                 className="mt-1 inline-flex items-center gap-1 text-[10.5px] text-primary hover:underline"
                 data-testid={`magnet-open-${it.book_id}`}>
                <ExternalLink className="h-3 w-3" /> Open public sample page
              </a>
            )}
          </Card>
        ))}
      </div>
    </Section>
  );
}
